import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {Button, EMPHASIS, Tooltip} from '@forecast-it/design-system';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import Util from '../../shared/util/util';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import {showProjectStageModal} from './modals/ProjectStageModal';
import type {SettingsProjectStages_viewer$key} from './__generated__/SettingsProjectStages_viewer.graphql';

const Page = Styled.div`
	max-width: 1000px;
	margin-left: 24px;
	margin-top: 6px;
	font-size: 13px;
`;

const StageList = Styled.ol`
	list-style: none;
	padding: 0;
	margin: 0 0 12px;
	max-width: 620px;
`;

const StageRow = Styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 40px;
	padding: 0 12px;
	border-bottom: 1px solid #e6e6e6;
`;

// Names the icon-only edit button: the design system's Button overwrites aria-label, so the name comes
// from this visually hidden element through aria-labelledby.
const AssistiveText = Styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip: rect(0 0 0 0);
	clip-path: inset(50%);
`;

export type ProjectStageRow = {
	__typename: string;
	id: string;
	name: string | null;
	defaultStage?: string | null;
	sortOrder: number | null;
};

const isCustom = (stage: ProjectStageRow) => stage.__typename === 'CustomProjectStage';

export const SettingsProjectStagesComponent = ({
	companyId,
	stages,
}: {
	companyId: string;
	stages: ReadonlyArray<ProjectStageRow>;
}): JSX.Element => {
	const intl = useIntl();
	const ordered = [...stages].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
	const editLabel = intl.formatMessage({id: 'settings.project_stages.edit'});

	// Default stages are translated from their default stage; custom stages show the name the admin gave.
	const label = (stage: ProjectStageRow) =>
		(!isCustom(stage) && stage.defaultStage && Util.getProjectStageTranslation(stage.defaultStage, intl)) ||
		stage.name ||
		'';

	// The duplicate check runs against stored names, so the defaults are compared by their English names
	// exactly as the server compares them.
	const namesExcept = (excludedId?: string) =>
		ordered.filter(stage => stage.id !== excludedId).map(stage => stage.name ?? '');

	const handleAdd = () => showProjectStageModal({companyId, otherNames: namesExcept()});

	const handleEdit = (stage: ProjectStageRow) =>
		showProjectStageModal({
			companyId,
			stage: {id: stage.id, name: stage.name ?? ''},
			otherNames: namesExcept(stage.id),
		});

	return (
		<div className="section-content settings-app" data-cy={'settings-project-stages-page'}>
			<ForecastThemeProvider>
				<>
					<SettingsProjectSubNav />
					<Page>
						<StageList data-cy={'project-stages-list'}>
							{ordered.map(stage => {
								const editLabelId = `project-stage-edit-label-${stage.id}`;
								return (
									<StageRow key={stage.id} data-cy={'project-stage-row'}>
										<span data-cy={'project-stage-name'}>{label(stage)}</span>
										{isCustom(stage) && (
											<>
												<AssistiveText id={editLabelId}>{editLabel}</AssistiveText>
												<Tooltip content={editLabel} interactable={false}>
													<Button
														emphasis={EMPHASIS.LOW}
														icon={'edit'}
														iconOnly
														onPress={() => handleEdit(stage)}
														aria-labelledby={editLabelId}
														data-cy={'project-stage-edit'}
													/>
												</Tooltip>
											</>
										)}
									</StageRow>
								);
							})}
						</StageList>
						<Button emphasis={EMPHASIS.LOW} icon={'plus'} onPress={handleAdd} data-cy={'project-stages-add'}>
							{intl.formatMessage({id: 'settings.project_stages.add'})}
						</Button>
					</Page>
				</>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsProjectStagesQuery = graphql`
	query SettingsProjectStagesQuery {
		viewer {
			actualPersonId
			component(name: "settings_project_stages")
			...SettingsProjectStages_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsProjectStages_viewer on Viewer {
		company {
			id
			projectStages(first: 1000) @connection(key: "Company_projectStages") {
				edges {
					node {
						__typename
						id
						name
						sortOrder
						... on DefaultProjectStage {
							defaultStage
						}
					}
				}
			}
		}
	}
`;

const SettingsProjectStages = ({viewer}: {viewer: SettingsProjectStages_viewer$key}): JSX.Element => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	const stages = (data.company?.projectStages?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	return <SettingsProjectStagesComponent companyId={data.company?.id ?? ''} stages={stages} />;
};

export default SettingsProjectStages;
